export const GENERATION_MODELS = [
  "wan2.7-image",
  "wan2.7-image-pro",
  "qwen-image-2.1-pro",
  "qwen-image-2.1-turbo",
] as const;

export type GenerationModel = typeof GENERATION_MODELS[number];

export interface ExecutionOptions {
  model: GenerationModel;
  promptExtend?: boolean;
}

export interface GenerationConfig {
  default: ExecutionOptions;
  models: Array<{
    name: GenerationModel;
    promptExtend: boolean;
    maxInputImages: number;
    maxInputBytes: number;
    minOutputPixels: number;
    maxOutputPixels: number;
  }>;
}

export interface RegenerationBody {
  executionOptions?: ExecutionOptions;
  useCutout?: boolean;
  expectedAttemptId: string;
}

export interface PendingRegeneration {
  key: string;
  body: RegenerationBody;
}

export interface RegenerationResponse {
  jobId: string;
  currentAttemptId?: string;
  executionOptions?: ExecutionOptions;
  useCutout?: boolean;
  retryIdempotencyKey?: string;
}

export function isQwenModel(model: GenerationModel): boolean {
  return model.startsWith("qwen-");
}

export function executionOptionsFor(model: GenerationModel, promptExtend = true): ExecutionOptions {
  return isQwenModel(model) ? { model, promptExtend } : { model };
}

const PENDING_STORAGE_KEY = "admin-faceswap-pending-regenerations-v1";

export function readPendingRegenerations(): Record<string, PendingRegeneration> {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(PENDING_STORAGE_KEY) || "{}");
    if (!value || typeof value !== "object" || Array.isArray(value)) return {};
    const result: Record<string, PendingRegeneration> = {};
    for (const [id, entry] of Object.entries(value)) {
      if (!entry || typeof entry !== "object") continue;
      const pending = entry as PendingRegeneration;
      const options = pending.body?.executionOptions;
      if (typeof pending.key !== "string" || !pending.key || typeof pending.body?.expectedAttemptId !== "string" || !pending.body.expectedAttemptId) continue;
      if (Object.prototype.hasOwnProperty.call(pending.body, "useCutout") && typeof pending.body.useCutout !== "boolean") continue;
      if (options && (!GENERATION_MODELS.includes(options.model) || (isQwenModel(options.model) ? typeof options.promptExtend !== "boolean" : options.promptExtend !== undefined))) continue;
      result[id] = pending;
    }
    return result;
  } catch {
    return {};
  }
}

export function persistPendingRegeneration(id: string, pending: PendingRegeneration | null, expectedKey?: string): boolean {
  const entries = readPendingRegenerations();
  if (pending) {
    entries[id] = pending;
  } else {
    if (!expectedKey || entries[id]?.key !== expectedKey) return false;
    delete entries[id];
  }
  localStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify(entries));
  return true;
}
